import { Component } from '@theme/component';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const MAX_VIEWED_PRODUCTS = 3;

/** @param {unknown} value */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** @param {unknown} value */
function idOf(value) {
  if (value === undefined || value === null || value === '') return '';
  return String(value);
}

/** @param {any} variant */
function optionsOfVariant(variant) {
  if (Array.isArray(variant?.options)) return variant.options.map((value) => String(value ?? ''));

  return [variant?.option1, variant?.option2, variant?.option3]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map((value) => String(value));
}

/** @param {any} product */
function variantsOfProduct(product) {
  return asArray(product?.variants);
}

/** @param {any} product */
function hasAvailableVariant(product) {
  return variantsOfProduct(product).some((variant) => variant?.available !== false);
}

/** @param {any} product */
function imageUrlOfProduct(product) {
  const featured = product?.featured_image ?? product?.featured_media?.preview_image ?? product?.image;

  if (typeof featured === 'string') return featured;
  if (typeof featured?.url === 'string') return featured.url;
  if (typeof featured?.src === 'string') return featured.src;
  if (typeof product?.images?.[0] === 'string') return product.images[0];
  if (typeof product?.images?.[0]?.src === 'string') return product.images[0].src;

  return '';
}

/**
 * The Product JSON endpoint returns prices in minor currency units. Keep the
 * formatting aligned with the active storefront locale without relying on the
 * browser's currency-symbol placement.
 *
 * @param {number|string|undefined} amount
 * @param {string} currency
 */
function formatMoney(amount, currency) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';

  const precision = ['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'].includes(currency) ? 3 : 2;
  const divisor = 10 ** precision;
  const locale = document.documentElement.lang.toLowerCase().startsWith('tr') ? 'tr-TR' : 'en-GB';
  const displayed = (value / divisor).toLocaleString(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  if (currency === 'TRY') return `${displayed} ₺`;
  return `${displayed} ${currency}`.trim();
}

/**
 * @param {any} product
 */
function optionNamesOfProduct(product) {
  const variants = variantsOfProduct(product);
  const configuredOptions = asArray(product?.options);
  const count = Math.max(configuredOptions.length, ...variants.map((variant) => optionsOfVariant(variant).length), 0);

  return Array.from({ length: count }, (_, index) => {
    const option = configuredOptions[index];
    if (typeof option === 'string' && option.trim()) return option;
    if (typeof option?.name === 'string' && option.name.trim()) return option.name;
    return `Seçenek ${index + 1}`;
  });
}

/** @param {any} product */
function productPrice(product) {
  const firstAvailable = variantsOfProduct(product).find((variant) => variant?.available !== false);
  return product?.price ?? product?.price_min ?? firstAvailable?.price ?? 0;
}

class GiestoCartUpsell extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Map<string, any>} */
  #viewedProductCache = new Map();

  /** @type {number} */
  #refreshVersion = 0;

  /** @type {Array<{product: any, parent: {id: string, handle: string}, rank: number}>} */
  #entries = [];

  /** @type {{product: any, parent: {id: string, handle: string}, rank: number} | null} */
  #activeEntry = null;

  /** @type {HTMLElement | null} */
  #activeOpener = null;

  /** @type {boolean} */
  #isAdding = false;

  /** @type {AbortController | null} */
  #railController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(RecentlyViewed.eventName, this.#handleRecentlyViewedUpdate, { signal });

    this.#refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#railController?.abort();
    this.#railController = null;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  #handleCartUpdate = (event) => {
    const refresh = () => window.setTimeout(() => this.#refresh(), 0);

    if (event.promise) {
      event.promise.then(refresh).catch(() => {
        // The add/change source owns its own error announcement. Keep this surface
        // intact so a shopper can try another viewed item.
      });
      return;
    }

    refresh();
  };

  #handleRecentlyViewedUpdate = () => {
    this.#refresh();
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const dialog = this.#dialog;
    if (dialog && event.target === dialog) {
      this.#closeDialog();
      return;
    }

    const closeButton = event.target.closest('[data-giesto-upsell-close]');
    if (closeButton) {
      this.#closeDialog();
      return;
    }

    const modalAddButton = event.target.closest('[data-giesto-upsell-modal-add]');
    if (modalAddButton instanceof HTMLButtonElement) {
      const variant = this.#selectedModalVariant();
      if (this.#activeEntry && variant) this.#addRecommendation(this.#activeEntry, variant);
      return;
    }

    const addButton = event.target.closest('[data-giesto-upsell-add]');
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled || this.#isAdding) return;

    const entry = this.#entries.find((candidate) => idOf(candidate.product?.id) === addButton.dataset.giestoUpsellAdd);
    if (!entry) return;

    const variants = variantsOfProduct(entry.product);
    if (variants.length > 1) {
      this.#openVariantDialog(entry, addButton);
      return;
    }

    const variant = variants.find((candidate) => candidate?.available !== false);
    if (variant) this.#addRecommendation(entry, variant);
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-giesto-upsell-option]')) {
      this.#syncModalVariant();
    }
  };

  async #refresh() {
    if (!this.isConnected) return;

    const version = ++this.#refreshVersion;
    const rail = this.#rail;
    rail?.setAttribute('aria-busy', 'true');

    try {
      const cart = await this.#fetchCart();
      if (version !== this.#refreshVersion || !this.isConnected) return;

      const cartItems = asArray(cart?.items);
      if (!cartItems.length) {
        this.#renderEntries([]);
        return;
      }

      const cartProductIds = new Set(
        cartItems
          .map((item) => idOf(item?.product_id ?? item?.product?.id))
          .filter(Boolean)
      );
      const entries = await this.#getViewedEntries(cartProductIds);

      if (version !== this.#refreshVersion || !this.isConnected) return;
      this.#renderEntries(entries);
    } catch (_error) {
      if (version === this.#refreshVersion && this.isConnected) this.#renderEntries([]);
    }
  }

  async #fetchCart() {
    const route = this.dataset.cartUrl || globalThis.Theme?.routes?.cart_url || '/cart';
    const url = route.endsWith('.js') ? route : `${route.replace(/\/$/, '')}.js`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  /**
   * Resolves the shopper's recently viewed history into sellable products.
   * Cart items and duplicate products are excluded while preserving recency.
   *
   * @param {Set<string>} cartProductIds
   * @returns {Promise<Array<{product: any, parent: {id: string, handle: string}, rank: number}>>}
   */
  async #getViewedEntries(cartProductIds) {
    const viewedProducts = RecentlyViewed.getProductDetails();
    if (!viewedProducts.length) return [];

    const products = await Promise.all(viewedProducts.map((product) => this.#fetchViewedProduct(product.handle)));
    const used = new Set(cartProductIds);
    /** @type {Array<{product: any, parent: {id: string, handle: string}, rank: number}>} */
    const entries = [];

    products.forEach((product) => {
      if (entries.length >= MAX_VIEWED_PRODUCTS || !product) return;

      const productId = idOf(product.id);
      if (!productId || used.has(productId) || !hasAvailableVariant(product)) return;

      used.add(productId);
      entries.push({
        product,
        parent: {
          id: productId,
          handle: String(product.handle ?? ''),
        },
        rank: entries.length + 1,
      });
    });

    return entries;
  }

  /** @param {string} handle */
  async #fetchViewedProduct(handle) {
    if (!handle) return null;
    if (this.#viewedProductCache.has(handle)) return this.#viewedProductCache.get(handle) ?? null;

    const productsUrl = this.dataset.productsUrl;
    if (!productsUrl) return null;

    const url = new URL(`${productsUrl.replace(/\/?$/, '/')}${encodeURIComponent(handle)}.js`, window.location.origin);

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return null;

      const product = await response.json();
      this.#viewedProductCache.set(handle, product);
      return product;
    } catch (_error) {
      return null;
    }
  }

  /** @param {any} product */
  #productUrl(product) {
    if (typeof product?.url === 'string' && product.url) return product.url;

    const handle = String(product?.handle ?? '');
    const productsUrl = this.dataset.productsUrl || '/products/';
    return `${productsUrl.replace(/\/?$/, '/')}${encodeURIComponent(handle)}`;
  }

  /** @param {Array<{product: any, parent: {id: string, handle: string}, rank: number}>} entries */
  #renderEntries(entries) {
    this.#entries = entries;
    this.#railController?.abort();
    this.#railController = null;

    const rail = this.#rail;
    if (!rail) return;

    rail.replaceChildren();
    rail.setAttribute('aria-busy', 'false');

    if (!entries.length) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    entries.forEach((entry) => rail.append(this.#createCard(entry)));
    this.#bindRailInteractions(rail);
  }

  /** @param {{product: any, parent: {id: string, handle: string}, rank: number}} entry */
  #createCard(entry) {
    const product = entry.product;
    const productId = idOf(product?.id);
    const title = String(product?.title ?? 'Ürün');
    const available = hasAvailableVariant(product);
    const card = document.createElement('article');
    card.className = 'giesto-cart-upsell__card';
    card.setAttribute('role', 'listitem');

    const link = document.createElement('a');
    link.className = 'giesto-cart-upsell__card-link';
    link.href = this.#productUrl(product);
    link.setAttribute('aria-label', title);

    const media = document.createElement('span');
    media.className = 'giesto-cart-upsell__media';
    const imageUrl = imageUrlOfProduct(product);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'giesto-cart-upsell__image';
      image.src = imageUrl;
      image.alt = title;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'giesto-cart-upsell__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      media.append(placeholder);
    }
    link.append(media);

    // Laid out like the collection card: title and price centred under the
    // image, then a full-width row holding add-to-bag and the favourite split
    // by a hairline. Measured off the collection grid at 390px — 12px/14px
    // uppercase title, 12px price, a 36px-tall action row, square corners.
    const meta = document.createElement('div');
    meta.className = 'giesto-cart-upsell__card-meta';

    const titleElement = document.createElement('a');
    titleElement.className = 'giesto-cart-upsell__title';
    titleElement.href = link.href;
    titleElement.textContent = title;

    const price = document.createElement('span');
    price.className = 'giesto-cart-upsell__price';
    price.textContent = formatMoney(productPrice(product), this.#currency);

    const actions = document.createElement('div');
    actions.className = 'giesto-cart-upsell__actions';

    const action = document.createElement('button');
    action.className = 'giesto-cart-upsell__action';
    action.type = 'button';
    action.dataset.giestoUpsellAdd = productId;
    action.disabled = !available;
    action.textContent = '+';
    action.setAttribute(
      'aria-label',
      available ? `${this.dataset.addLabel ?? 'SEPETE EKLE'}: ${title}` : `${this.dataset.soldOutLabel ?? 'TÜKENDİ'}: ${title}`
    );

    const divider = document.createElement('span');
    divider.className = 'giesto-cart-upsell__divider';
    divider.setAttribute('aria-hidden', 'true');

    actions.append(action, divider, this.#buildFavourite(product, productId, title, imageUrl));
    meta.append(titleElement, price, actions);
    card.append(link, meta);
    return card;
  }

  /**
   * The same `<giesto-wishlist-toggle>` the collection card uses, so the heart
   * shares one store (assets/giesto-wishlist.js) and one filled/empty state
   * across the grid, the cart drawer and the favourites panel.
   *
   * @param {any} product
   * @param {string} productId
   * @param {string} title
   * @param {string} imageUrl
   */
  #buildFavourite(product, productId, title, imageUrl) {
    const toggle = document.createElement('giesto-wishlist-toggle');
    toggle.className = 'giesto-cart-upsell__fav';
    toggle.dataset.productId = productId;
    toggle.dataset.productHandle = String(product?.handle ?? '');
    toggle.dataset.productTitle = title;
    toggle.dataset.productUrl = this.#productUrl(product);
    toggle.dataset.productImage = imageUrl || '';
    toggle.dataset.productPrice = formatMoney(productPrice(product), this.#currency);

    const addLabel = this.dataset.favouriteAddLabel || 'Favorilere ekle';
    const removeLabel = this.dataset.favouriteRemoveLabel || 'Favorilerden çıkar';
    toggle.dataset.labelAdd = `${addLabel}: ${title}`;
    toggle.dataset.labelRemove = `${removeLabel}: ${title}`;

    const button = document.createElement('button');
    button.className = 'giesto-cart-upsell__fav-button';
    button.type = 'button';
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', `${addLabel}: ${title}`);
    button.innerHTML =
      '<svg class="giesto-cart-upsell__heart" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M20 8.8c0 5-8 10.1-8 10.1S4 13.8 4 8.8C4 6.6 5.7 5 7.8 5c1.8 0 3.3 1 4.2 2.4C12.9 6 14.4 5 16.2 5 18.3 5 20 6.6 20 8.8Z" ' +
      'stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" fill="none"/></svg>';

    toggle.append(button);
    return toggle;
  }

  /** @param {HTMLElement} rail */
  #bindRailInteractions(rail) {
    this.#railController = new AbortController();
    const { signal } = this.#railController;
    let drag = null;
    let suppressClick = false;

    const endDrag = (event) => {
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;

      if (rail.hasPointerCapture?.(drag.pointerId)) rail.releasePointerCapture(drag.pointerId);
      if (drag.moved) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }

      drag = null;
      rail.removeAttribute('data-giesto-upsell-dragging');
    };

    rail.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('a, button, select')) return;

        drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: rail.scrollLeft,
          moved: false,
        };
        rail.setPointerCapture?.(event.pointerId);
      },
      { signal }
    );

    rail.addEventListener(
      'pointermove',
      (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;

        const delta = event.clientX - drag.startX;
        if (!drag.moved && Math.abs(delta) < 5) return;

        drag.moved = true;
        rail.dataset.giestoUpsellDragging = 'true';
        rail.scrollLeft = drag.startScrollLeft - delta;
        event.preventDefault();
      },
      { signal }
    );

    rail.addEventListener('pointerup', endDrag, { signal });
    rail.addEventListener('pointercancel', endDrag, { signal });
    rail.addEventListener('lostpointercapture', endDrag, { signal });

    rail.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );

    rail.addEventListener(
      'wheel',
      (event) => {
        const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!horizontalIntent && !event.shiftKey) return;

        const delta = horizontalIntent ? event.deltaX : event.deltaY;
        const next = Math.max(0, Math.min(rail.scrollLeft + delta, rail.scrollWidth - rail.clientWidth));
        if (Math.abs(next - rail.scrollLeft) < 0.5) return;

        rail.scrollLeft = next;
        event.preventDefault();
      },
      { passive: false, signal }
    );

    rail.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        const card = rail.querySelector('.giesto-cart-upsell__card');
        const amount = card instanceof HTMLElement ? card.offsetWidth + 5 : rail.clientWidth * 0.8;
        rail.scrollBy({
          left: event.key === 'ArrowLeft' ? -amount : amount,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        event.preventDefault();
      },
      { signal }
    );
  }

  /** @param {{product: any, parent: {id: string, handle: string}, rank: number}} entry @param {HTMLElement} opener */
  #openVariantDialog(entry, opener) {
    const dialog = this.#dialog;
    const content = this.querySelector('[data-giesto-upsell-modal-content]');
    if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement)) return;

    this.#activeEntry = entry;
    this.#activeOpener = opener;
    const product = entry.product;
    const title = String(product?.title ?? 'Seçilen ürün');
    const imageUrl = imageUrlOfProduct(product);
    const productInfo = document.createElement('div');
    productInfo.className = 'giesto-cart-upsell__dialog-product';

    const media = document.createElement('div');
    media.className = 'giesto-cart-upsell__dialog-media';
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'giesto-cart-upsell__dialog-image';
      image.src = imageUrl;
      image.alt = title;
      image.decoding = 'async';
      media.append(image);
    }

    const copy = document.createElement('div');
    const kicker = document.createElement('p');
    kicker.className = 'giesto-cart-upsell__dialog-kicker';
    kicker.textContent = this.dataset.selectLabel ?? 'BEDEN / RENK SEÇ';
    const titleElement = document.createElement('p');
    titleElement.id = `${this.id}-dialog-title`;
    titleElement.className = 'giesto-cart-upsell__dialog-title';
    titleElement.textContent = title;
    const priceElement = document.createElement('p');
    priceElement.className = 'giesto-cart-upsell__dialog-price';
    priceElement.dataset.giestoUpsellModalPrice = '';
    copy.append(kicker, titleElement, priceElement);
    productInfo.append(media, copy);

    const options = document.createElement('fieldset');
    options.className = 'giesto-cart-upsell__options';
    options.dataset.giestoUpsellOptions = '';
    const variants = variantsOfProduct(product);
    const firstAvailable = variants.find((variant) => variant?.available !== false) ?? variants[0];
    const optionNames = optionNamesOfProduct(product);
    const defaultValues = optionsOfVariant(firstAvailable);

    optionNames.forEach((name, index) => {
      const wrapper = document.createElement('label');
      wrapper.className = 'giesto-cart-upsell__option';
      const label = document.createElement('span');
      label.className = 'giesto-cart-upsell__option-label';
      label.textContent = name;
      const select = document.createElement('select');
      select.className = 'giesto-cart-upsell__option-select';
      select.dataset.giestoUpsellOption = String(index);
      select.setAttribute('aria-label', name);

      const values = [...new Set(variants.map((variant) => optionsOfVariant(variant)[index]).filter(Boolean))];
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = value === defaultValues[index];
        select.append(option);
      });

      wrapper.append(label, select);
      options.append(wrapper);
    });

    const addButton = document.createElement('button');
    addButton.className = 'giesto-cart-upsell__dialog-add';
    addButton.type = 'button';
    addButton.dataset.giestoUpsellModalAdd = '';
    addButton.textContent = this.dataset.addLabel ?? 'SEPETE EKLE';

    content.replaceChildren(productInfo, options, addButton);
    this.#syncModalVariant();
    dialog.addEventListener('close', this.#handleDialogClose, { once: true });

    if (!dialog.open) dialog.showModal();
  }

  #syncModalVariant() {
    if (!this.#activeEntry) return;

    const options = [...this.querySelectorAll('[data-giesto-upsell-option]')].filter(
      (select) => select instanceof HTMLSelectElement
    );
    const variants = variantsOfProduct(this.#activeEntry.product);

    for (let pass = 0; pass < 2; pass += 1) {
      options.forEach((select, index) => {
        const selectedValues = options.map((control) => control.value);

        [...select.options].forEach((option) => {
          option.disabled = !variants.some((variant) => {
            if (variant?.available === false || optionsOfVariant(variant)[index] !== option.value) return false;
            const variantValues = optionsOfVariant(variant);
            return selectedValues.every(
              (selectedValue, optionIndex) => optionIndex === index || !selectedValue || variantValues[optionIndex] === selectedValue
            );
          });
        });

        if (select.selectedOptions[0]?.disabled) {
          const availableOption = [...select.options].find((option) => !option.disabled);
          if (availableOption) select.value = availableOption.value;
        }
      });
    }

    const variant = this.#selectedModalVariant();
    const addButton = this.querySelector('[data-giesto-upsell-modal-add]');
    const price = this.querySelector('[data-giesto-upsell-modal-price]');

    if (addButton instanceof HTMLButtonElement) addButton.disabled = this.#isAdding || !variant;
    if (price instanceof HTMLElement) price.textContent = formatMoney(variant?.price ?? productPrice(this.#activeEntry.product), this.#currency);
  }

  #selectedModalVariant() {
    if (!this.#activeEntry) return null;

    const selectedValues = [...this.querySelectorAll('[data-giesto-upsell-option]')]
      .filter((select) => select instanceof HTMLSelectElement)
      .map((select) => /** @type {HTMLSelectElement} */ (select).value);

    return (
      variantsOfProduct(this.#activeEntry.product).find((variant) => {
        if (variant?.available === false) return false;
        const values = optionsOfVariant(variant);
        return selectedValues.every((value, index) => values[index] === value);
      }) ?? null
    );
  }

  #closeDialog() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
  }

  #handleDialogClose = () => {
    const opener = this.#activeOpener;
    this.#activeEntry = null;
    this.#activeOpener = null;

    if (opener?.isConnected) opener.focus();
  };

  /** @param {{product: any, parent: {id: string, handle: string}, rank: number}} entry @param {any} variant */
  async #addRecommendation(entry, variant) {
    if (this.#isAdding || !variant?.id) return;

    this.#isAdding = true;
    this.#setControlsBusy(true);
    this.#setStatus('Ürün sepete ekleniyor.');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const source = this.dataset.surface === 'cart-drawer' ? 'cart-drawer' : 'cart';
    const itemQuantity = 1;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity: itemQuantity }],
        promise: deferredEventPromise.promise,
        detail: { source: 'giesto-cart-upsell' },
      })
    );

    try {
      const response = await this.#postCartAdd({
        items: [
          {
            id: Number(variant.id),
            quantity: itemQuantity,
            properties: {
              _giesto_upsell_source: source,
              _giesto_upsell_parent_product_id: entry.parent.id,
              _giesto_upsell_parent_product_handle: entry.parent.handle,
              _giesto_upsell_position: String(entry.rank),
            },
          },
        ],
        sections: this.#cartSectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const cart = await this.#fetchCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: itemQuantity,
          source: 'giesto-cart-upsell',
          sections: response.sections,
        },
      });

      this.#setStatus(this.dataset.addedLabel ?? 'ÜRÜN SEPETE EKLENDİ');
      this.#closeDialog();
      this.#openNativeCart();
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : this.dataset.errorLabel ?? 'Ürün sepete eklenemedi.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setStatus(this.dataset.errorLabel ?? 'Ürün şu anda sepete eklenemedi. Lütfen tekrar deneyin.');
    } finally {
      this.#isAdding = false;
      this.#setControlsBusy(false);
    }
  }

  /** @param {Record<string, unknown>} payload */
  async #postCartAdd(payload) {
    const route = this.dataset.cartAddUrl || globalThis.Theme?.routes?.cart_add_url || '/cart/add';
    const url = route.endsWith('.js') ? route : `${route.replace(/\/$/, '')}.js`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data?.status || data?.errors) {
      throw new Error(data?.description || data?.message || 'Ürün sepete eklenemedi.');
    }

    return data;
  }

  /** @param {boolean} busy */
  #setControlsBusy(busy) {
    for (const button of this.querySelectorAll('[data-giesto-upsell-add]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const entry = this.#entries.find((candidate) => idOf(candidate.product?.id) === button.dataset.giestoUpsellAdd);
      button.disabled = busy || !entry || !hasAvailableVariant(entry.product);
    }

    this.#syncModalVariant();
  }

  #setStatus(message) {
    const status = this.querySelector('[data-giesto-upsell-status]');
    if (status instanceof HTMLElement) status.textContent = message;
  }

  #openNativeCart() {
    // The global confirmation panel replaces the drawer's post-add auto-open.
    // Keep this fallback for layouts that intentionally omit the panel.
    if (document.querySelector('giesto-store-toast[data-suppress-cart-drawer]')) return;

    if (this.dataset.surface !== 'cart-drawer') return;

    const drawer = this.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  get #rail() {
    const rail = this.querySelector('[data-giesto-upsell-rail]');
    return rail instanceof HTMLElement ? rail : null;
  }

  get #dialog() {
    const dialog = this.querySelector('[data-giesto-upsell-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #currency() {
    return this.dataset.currency || 'TRY';
  }

  get #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
      .filter(Boolean);
  }
}

if (!customElements.get('giesto-cart-upsell')) {
  customElements.define('giesto-cart-upsell', GiestoCartUpsell);
}
